import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { Button } from '@roger/ui-components';
import {
  deleteMedicationProfileAttachment,
  getMedicationAttachmentUrl,
  getMedicationProfileAttachments,
  uploadMedicationProfileAttachment,
} from '../../services/api';
import type { MedicationProfileAttachmentResponse } from '../../services/api';
import AttachmentRow from './AttachmentRow';
import AttachmentsEmptyState from './AttachmentsEmptyState';

interface AttachmentsCardProps {
  patientId: string;
}

const AttachmentsCard: React.FC<AttachmentsCardProps> = ({ patientId }) => {
  const [attachments, setAttachments] = useState<
    MedicationProfileAttachmentResponse[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchAttachments = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getMedicationProfileAttachments({
        path: { patient_id: patientId },
      });
      if (response?.data) {
        setAttachments(response.data);
      } else {
        setAttachments([]);
      }
    } catch (err) {
      console.error('Failed to fetch attachments:', err);
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const response = await uploadMedicationProfileAttachment({
        path: { patient_id: patientId },
        body: { file },
      });

      if (response?.data) {
        setAttachments(prev => [response.data!, ...prev]);
      }
    } catch (err) {
      console.error('Failed to upload attachment:', err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadAttachment = async (
    attachment: MedicationProfileAttachmentResponse
  ) => {
    try {
      const response = await getMedicationAttachmentUrl({
        path: {
          patient_id: patientId,
          attachment_id: attachment.id,
        },
      });

      if (response?.data) {
        const data = response.data as { url?: string };
        if (data.url) {
          window.open(data.url, '_blank');
        }
      }
    } catch (err) {
      console.error('Failed to get download URL:', err);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    await deleteMedicationProfileAttachment({
      path: {
        patient_id: patientId,
        attachment_id: attachmentId,
      },
    });

    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  return (
    <div className='mt-6 pt-6 border-t'>
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h4 className='text-sm font-medium text-foreground'>Attachments</h4>
          <p className='text-xs text-muted-foreground mt-0.5'>
            Supporting documents for medication profile
          </p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type='file'
            className='hidden'
            onChange={handleFileSelect}
            accept='.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif'
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            variant='outline'
            size='sm'
          >
            {isUploading ? (
              <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
            ) : (
              <Upload className='h-3.5 w-3.5 mr-1.5' />
            )}
            Upload
          </Button>
        </div>
      </div>
      {isLoading ? (
        <div className='flex items-center justify-center py-6'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        </div>
      ) : attachments.length === 0 ? (
        <AttachmentsEmptyState />
      ) : (
        <div className='space-y-2'>
          {attachments.map(attachment => (
            <AttachmentRow
              key={attachment.id}
              attachment={attachment}
              onDownload={handleDownloadAttachment}
              onDelete={handleDeleteAttachment}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AttachmentsCard;
